import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useAddManualEvent } from "../lib/customers";
import {
  shouldRenderInsertMark,
  shouldWarnAboutOlderEvents,
} from "../lib/journey-insert-guard";
import {
  computeInsertTimestamp,
  recomputeInsertState,
  resolveInsertDate,
  toLocalInputValue,
} from "../lib/journey-insert-position";
import {
  useMetaCampaigns,
  useMetaAdSets,
  useMetaAds,
  type MetaTreeError,
} from "../lib/meta-campaign-tree";
import { shouldFetchAllChildren, shouldWidenChildFetch } from "../lib/meta-picker-level";
import { Combobox } from "./Combobox";
import { ApiError } from "../lib/api";

/** Known error codes from POST .../customers/:visitorId/events (F4, TASK-59
 *  review) — an unmapped code still falls back to the raw ApiError message,
 *  never silently swallowed. Exported: the approval-drawer touchpoint form
 *  (TASK-76) writes through the same endpoint and hits the same codes. */
export const INSERT_ERROR_MESSAGES: Record<string, string> = {
  visitor_has_no_keys:
    "Dieser Kontakt hat keine Identität (E-Mail, Telefon o.Ä.) — Touchpoint kann nicht zugeordnet werden.",
  visitor_not_found:
    "Dieser Kontakt wurde nicht gefunden — evtl. wurde er inzwischen zusammengeführt oder gelöscht.",
  not_found: "Dieses Projekt wurde nicht gefunden.",
};

interface Props {
  projectId: number;
  visitorId: number;
  /** createdAt of the journey step right before this position, null at the
   *  very front (or when the journey is empty). */
  prevAt: string | null;
  /** createdAt of the journey step right after this position, null at the
   *  very end (or when the journey is empty). */
  nextAt: string | null;
  /** Count of not-yet-loaded older events in long-term storage (Story S6,
   *  Epic A4) — reuses CustomerDetail's existing completeness signal
   *  (`olderInBq`). Only meaningful at the frontmost mark; the guard warns
   *  there regardless of which marks it's passed to. */
  olderCount?: number;
  /** Loads the next page of older events (CustomerDetail's
   *  `loadMoreFromBq`) — offered right on the warning so "ganz vorne" can
   *  be fixed before inserting instead of after. */
  onLoadOlder?: () => void;
  loadingOlder?: boolean;
}

/**
 * One insertion point in a contact's journey (TASK-59, Epic A4). Renders as
 * a marker between two steps — reachable and operable by keyboard like any
 * other button, not just on mouse hover (AC).
 *
 * Variante B from the prototype (docs/discovery/attribution-korrektur/epic.md,
 * "Erscheinungsbild" — eingefroren): the form opens right at this position
 * in the journey list, no dialog window.
 *
 * Hidden by default behind the "Touchpoint einfügen" toggle in
 * CustomerDetail (TASK-75, manual acceptance follow-up) — `visible` reflects
 * that toggle. An already-open form stays rendered even while `visible` is
 * false (`shouldRenderInsertMark`) so switching the toggle off never makes a
 * form the operator is mid-way through vanish silently (issue AC).
 */
export function InsertTouchpointMark({
  projectId,
  visitorId,
  prevAt,
  nextAt,
  visible,
  olderCount = 0,
  onLoadOlder,
  loadingOlder = false,
}: Props & { visible: boolean }) {
  const [open, setOpen] = useState(false);

  if (!shouldRenderInsertMark(visible, open)) return null;

  return (
    <>
      {/* Deliberately NOT gated on `visible`: shouldRenderInsertMark above
          already returned null when neither the toggle is on nor a form is
          open. Keeping the warning tied to `visible` would drop it exactly
          when an open front form survives the toggle being switched off —
          the moment someone is inserting at the very front and needs it. */}
      {shouldWarnAboutOlderEvents(prevAt, olderCount) && (
        <li className="flex items-start gap-2 rounded-md border border-warning bg-warning-bg/40 px-3 py-2 text-xs text-text-primary">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
          <div className="flex-1">
            Noch <strong>{olderCount}</strong> ältere Ereignisse nicht
            geladen — „ganz vorne" ist hier evtl. nicht der tatsächliche
            Anfang.
          </div>
          {onLoadOlder && (
            <button
              type="button"
              onClick={onLoadOlder}
              disabled={loadingOlder}
              className="shrink-0 rounded-md border border-warning px-2 py-1 font-medium text-warning hover:bg-warning-bg disabled:opacity-50"
            >
              {loadingOlder ? "Lädt…" : "Jetzt laden"}
            </button>
          )}
        </li>
      )}
      {!open ? (
        <li>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="group flex w-full items-center gap-2 py-1"
          >
            <span className="h-px flex-1 bg-border-primary group-hover:bg-accent" />
            <span className="flex items-center gap-1 rounded-full border border-border-primary bg-bg-secondary px-2 py-0.5 font-mono text-[9px] font-medium uppercase tracking-wide text-text-tertiary group-hover:border-accent group-hover:text-accent">
              <Plus size={10} /> Touchpoint einfügen
            </span>
            <span className="h-px flex-1 bg-border-primary group-hover:bg-accent" />
          </button>
        </li>
      ) : (
        <li>
          <InsertTouchpointForm
            projectId={projectId}
            visitorId={visitorId}
            prevAt={prevAt}
            nextAt={nextAt}
            onClose={() => setOpen(false)}
          />
        </li>
      )}
    </>
  );
}

/** Exported: reused by the approval-drawer touchpoint form (TASK-76) so both
 *  forms share the same input styling instead of a second copy drifting. */
export const FIELD_CLASS =
  "h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none";

export function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}

function InsertTouchpointForm({
  projectId,
  visitorId,
  prevAt,
  nextAt,
  onClose,
}: Props & { onClose: () => void }) {
  // Computed at mount and kept in sync below — stable only while the
  // operator hasn't edited the field by hand (AC: "Der berechnete Zeitpunkt
  // wird vor dem Einfügen angezeigt").
  const [computed, setComputed] = useState(() =>
    computeInsertTimestamp(prevAt, nextAt),
  );
  const [datetime, setDatetime] = useState(
    computed ? toLocalInputValue(computed) : "",
  );
  // Whether the operator touched the time field by hand — while false,
  // `computed` (full ms precision) is what gets submitted, not the
  // minute-truncated display value (F1, TASK-59 review).
  const [datetimeEdited, setDatetimeEdited] = useState(false);
  // Neighbors can shift while the form stays open — e.g. "Jetzt laden" pulls
  // an older event in ahead of the frontmost mark. Re-derive computed/
  // datetime so an unedited submit never silently lands behind newly loaded
  // events; a manual edit is left alone (F1, TASK-61 review).
  useEffect(() => {
    const next = recomputeInsertState(prevAt, nextAt, datetimeEdited);
    setComputed(next.computed);
    if (next.datetime !== null) setDatetime(next.datetime);
  }, [prevAt, nextAt, datetimeEdited]);
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  // Kampagne: Meta-Auswahl (dh_c/dh_as/dh_a + Name) oder freier Text,
  // je nachdem ob MetaCampaignFields ein Werbekonto auflösen kann (TASK-60).
  const [campaignAttribution, setCampaignAttribution] = useState<CampaignAttribution>({
    utmCampaign: "",
  });
  const add = useAddManualEvent(projectId);

  const canSubmit =
    datetime.length > 0 && utmSource.trim().length > 0 && !add.isPending;

  const submit = () => {
    if (!canSubmit) return;
    const createdAt = resolveInsertDate(computed, datetimeEdited, datetime);
    if (Number.isNaN(createdAt.getTime())) {
      toast.error("Zeitpunkt ungültig");
      return;
    }
    add.mutate(
      {
        visitorId,
        // TASK-151: PageView, not Interaction (TASK-59's original choice) —
        // the Ads-Manager, Kunden-Hub and /v1 customer API only treat
        // PageView rows as a campaign touchpoint.
        eventType: "PageView",
        createdAt: createdAt.toISOString(),
        utmSource: utmSource.trim(),
        ...(utmMedium.trim() ? { utmMedium: utmMedium.trim() } : {}),
        ...(campaignAttribution.utmCampaign.trim()
          ? { utmCampaign: campaignAttribution.utmCampaign.trim() }
          : {}),
        ...(campaignAttribution.utmContent?.trim()
          ? { utmContent: campaignAttribution.utmContent.trim() }
          : {}),
        ...(campaignAttribution.utmTerm?.trim()
          ? { utmTerm: campaignAttribution.utmTerm.trim() }
          : {}),
        ...(campaignAttribution.dhC ? { dhC: campaignAttribution.dhC } : {}),
        ...(campaignAttribution.dhAs ? { dhAs: campaignAttribution.dhAs } : {}),
        ...(campaignAttribution.dhA ? { dhA: campaignAttribution.dhA } : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            "Touchpoint in die Pipeline geschrieben — erscheint in wenigen Sekunden in der Journey.",
          );
          onClose();
        },
        onError: (err) => {
          const code = (
            (err as ApiError)?.body as { error?: string } | undefined
          )?.error;
          toast.error(
            (code && INSERT_ERROR_MESSAGES[code]) ??
              `Einfügen fehlgeschlagen: ${(err as Error).message}`,
          );
        },
      },
    );
  };

  return (
    <div className="space-y-3 rounded-md border border-accent/40 bg-bg-secondary p-3">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
          Touchpoint einfügen
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Abbrechen"
          className="text-text-tertiary hover:text-text-primary"
        >
          <X size={14} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Quelle">
          <input
            type="text"
            autoFocus
            value={utmSource}
            onChange={(e) => setUtmSource(e.target.value)}
            placeholder="z.B. facebook"
            className={FIELD_CLASS}
          />
        </Field>
        <Field label="Medium">
          <input
            type="text"
            value={utmMedium}
            onChange={(e) => setUtmMedium(e.target.value)}
            placeholder="z.B. cpc"
            className={FIELD_CLASS}
          />
        </Field>
      </div>

      <MetaCampaignFields
        projectId={projectId}
        value={campaignAttribution}
        onChange={setCampaignAttribution}
      />

      <Field label="Zeitpunkt">
        <input
          type="datetime-local"
          step="1"
          value={datetime}
          onChange={(e) => {
            setDatetime(e.target.value);
            setDatetimeEdited(true);
          }}
          className={FIELD_CLASS}
        />
      </Field>
      <p className="text-xs text-text-tertiary">
        {computed
          ? "Automatisch zwischen die Nachbar-Ereignisse gelegt — bei Bedarf anpassen."
          : "Leere Journey: Zeitpunkt von Hand eingeben."}
      </p>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {add.isPending && <Loader2 size={13} className="animate-spin" />}
          Einfügen
        </button>
      </div>
    </div>
  );
}

/** Campaign/ad-set/ad chosen via the Meta picker, or free text when Meta
 *  isn't available/resolvable (TASK-60, S3). `dh*` stay undefined in the
 *  free-text case — the write path only sets what's present. Exported: the
 *  approval-drawer touchpoint form (TASK-76) shares this shape.
 *
 *  utmContent/utmTerm (TASK-81) mirror utmCampaign one level down each: the
 *  ad set's name and the ad's name, alongside their dhAs/dhA ids. Left
 *  undefined — not a placeholder, not the id — when that level isn't
 *  chosen. */
export interface CampaignAttribution {
  utmCampaign: string;
  utmContent?: string;
  utmTerm?: string;
  dhC?: string;
  dhAs?: string;
  dhA?: string;
}

const META_UNAVAILABLE_HINT: Record<MetaTreeError, string> = {
  no_ad_account: "Kein Werbekonto für dieses Projekt verknüpft.",
  not_configured: "Meta-Zugang ist nicht eingerichtet.",
  meta_error: "Meta ist gerade nicht erreichbar.",
};

/** F1, TASK-60 review: the fetch hit its hard page cap (extreme inventory
 *  size) — the list shown is real but possibly incomplete, rather than
 *  silently missing entries. */
const META_TRUNCATED_HINT =
  "Liste evtl. unvollständig (sehr viele Einträge).";

/**
 * Kampagne/Anzeigengruppe/Anzeige nach Namen wählen (TASK-60, Epic A5).
 * Ebenen sind voneinander abhängig — eine Anzeigengruppe gehört immer zur
 * gewählten Kampagne, eine Anzeige immer zur gewählten Anzeigengruppe; die
 * Anzeigen-Ebene wird nie ungefiltert geladen (immer an eine Anzeigengruppe
 * gebunden). Ist kein Werbekonto auflösbar oder Meta nicht erreichbar,
 * bleibt „Kampagne" ein freies Textfeld mit Klartext-Hinweis — der
 * TASK-59-Zustand als Rückfallebene, die Funktion fällt nie ganz aus.
 *
 * Exported: reused as-is by the approval-drawer touchpoint form (TASK-76,
 * issue text: "wird wiederverwendet, nicht nachgebaut") — no second Meta
 * picker.
 */
export function MetaCampaignFields({
  projectId,
  value,
  onChange,
  portalContainer,
}: {
  projectId: number;
  value: CampaignAttribution;
  onChange: (next: CampaignAttribution) => void;
  /** Forwarded to each Combobox (TASK-80) — set by a caller rendering this
   *  inside a modal dialog (the approval drawer). Undefined for the journey
   *  form, which isn't in a dialog and keeps the Combobox default. */
  portalContainer?: Element | null;
}) {
  const [campaign, setCampaign] = useState<{ id: string; name: string } | null>(null);
  const [adSet, setAdSet] = useState<{ id: string; name: string } | null>(null);
  const [ad, setAd] = useState<{ id: string; name: string } | null>(null);

  // Aktive Ansicht ist der Default (S3); sobald im jeweiligen Feld getippt
  // wird, weitet sich der Abruf auf den Gesamtbestand (Combobox.onQueryChange)
  // — die Namenssuche selbst filtert clientseitig innerhalb der Combobox.
  const [campaignSearching, setCampaignSearching] = useState(false);
  const campaigns = useMetaCampaigns(projectId, campaignSearching);
  // Zwei getrennte Signale pro Kind-Ebene statt einem gemeinsamen Flag
  // (TASK-74 Review-Folgefund F1): *Searching* kommt ausschließlich aus dem
  // Sucheingabefeld dieser Ebene, *WidenedByStatus* ausschließlich aus dem
  // Status der gewählten Eltern-Ebene (shouldWidenChildFetch). Gemeinsam
  // per shouldFetchAllChildren (ODER) an den Abruf übergeben — sonst löscht
  // ein geleertes Suchfeld die statusbedingte Weitung mit.
  const [adSetSearching, setAdSetSearching] = useState(false);
  const [adSetWidenedByStatus, setAdSetWidenedByStatus] = useState(false);
  const adSets = useMetaAdSets(
    campaign?.id ?? null,
    shouldFetchAllChildren(adSetWidenedByStatus, adSetSearching),
  );
  const [adSearching, setAdSearching] = useState(false);
  const [adWidenedByStatus, setAdWidenedByStatus] = useState(false);
  const ads = useMetaAds(adSet?.id ?? null, shouldFetchAllChildren(adWidenedByStatus, adSearching));

  const report = (next: {
    campaign: typeof campaign;
    adSet: typeof adSet;
    ad: typeof ad;
  }) => {
    onChange({
      utmCampaign: next.campaign?.name ?? "",
      utmContent: next.adSet?.name,
      utmTerm: next.ad?.name,
      dhC: next.campaign?.id,
      dhAs: next.adSet?.id,
      dhA: next.ad?.id,
    });
  };

  if (campaigns.isLoading) {
    return (
      <Field label="Kampagne">
        <input className={FIELD_CLASS} disabled placeholder="Lädt Kampagnen…" />
      </Field>
    );
  }

  // Fehlerfall (A5): kein Werbekonto auflösbar oder Meta nicht erreichbar —
  // Kampagne bleibt als freier Text bedienbar (TASK-59-Zustand).
  if (!campaigns.data || !campaigns.data.ok) {
    const hint = campaigns.data
      ? META_UNAVAILABLE_HINT[campaigns.data.error]
      : "Kampagnen konnten nicht geladen werden.";
    return (
      <Field label="Kampagne">
        <input
          type="text"
          value={value.utmCampaign}
          onChange={(e) => onChange({ utmCampaign: e.target.value })}
          placeholder="z.B. Sommer-Kampagne"
          className={FIELD_CLASS}
        />
        <p className="mt-1 text-[11px] text-text-tertiary">
          {hint} Kampagne bleibt als Text eintragbar.
        </p>
      </Field>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <Field label="Kampagne">
        <Combobox
          value={campaign?.id ?? ""}
          options={campaigns.data.items.map((c) => ({ value: c.id, label: c.name }))}
          onChange={(id) => {
            const found = campaigns.data!.ok
              ? (campaigns.data!.items.find((c) => c.id === id) ?? null)
              : null;
            const next = found ? { id: found.id, name: found.name } : null;
            setCampaign(next);
            setAdSet(null);
            setAd(null);
            // TASK-74: a non-active campaign practically never has active ad
            // sets — widen the child fetch right away instead of forcing a
            // search keystroke first. Kept separate from adSetSearching (the
            // search box's own signal) so a later cleared search can't
            // override this (F1 follow-up).
            setAdSetWidenedByStatus(shouldWidenChildFetch(found?.status));
            setAdSetSearching(false);
            setAdWidenedByStatus(false);
            setAdSearching(false);
            report({ campaign: next, adSet: null, ad: null });
          }}
          onQueryChange={(q) => setCampaignSearching(q.trim().length > 0)}
          placeholder="Kampagne suchen…"
          portalContainer={portalContainer}
        />
        {campaigns.data.truncated && (
          <p className="mt-1 text-[11px] text-text-tertiary">{META_TRUNCATED_HINT}</p>
        )}
      </Field>
      <Field label="Anzeigengruppe">
        <Combobox
          value={adSet?.id ?? ""}
          options={adSets.data?.ok ? adSets.data.items.map((a) => ({ value: a.id, label: a.name })) : []}
          onChange={(id) => {
            const found = adSets.data?.ok
              ? (adSets.data.items.find((a) => a.id === id) ?? null)
              : null;
            const next = found ? { id: found.id, name: found.name } : null;
            setAdSet(next);
            setAd(null);
            // TASK-74: same widening one level down, for ads of a non-active
            // ad set. Kept separate from adSearching for the same reason as
            // above (F1 follow-up).
            setAdWidenedByStatus(shouldWidenChildFetch(found?.status));
            setAdSearching(false);
            report({ campaign, adSet: next, ad: null });
          }}
          onQueryChange={(q) => setAdSetSearching(q.trim().length > 0)}
          disabled={!campaign}
          placeholder={campaign ? "Anzeigengruppe suchen…" : "Erst Kampagne wählen"}
          portalContainer={portalContainer}
        />
        {adSet === null && adSets.data && !adSets.data.ok && (
          <p className="mt-1 text-[11px] text-text-tertiary">{META_UNAVAILABLE_HINT[adSets.data.error]}</p>
        )}
        {adSets.data?.ok && adSets.data.truncated && (
          <p className="mt-1 text-[11px] text-text-tertiary">{META_TRUNCATED_HINT}</p>
        )}
      </Field>
      <Field label="Anzeige">
        <Combobox
          value={ad?.id ?? ""}
          options={ads.data?.ok ? ads.data.items.map((a) => ({ value: a.id, label: a.name })) : []}
          onChange={(id) => {
            const found = ads.data?.ok ? (ads.data.items.find((a) => a.id === id) ?? null) : null;
            const next = found ? { id: found.id, name: found.name } : null;
            setAd(next);
            report({ campaign, adSet, ad: next });
          }}
          onQueryChange={(q) => setAdSearching(q.trim().length > 0)}
          disabled={!adSet}
          placeholder={adSet ? "Anzeige suchen…" : "Erst Anzeigengruppe wählen"}
          portalContainer={portalContainer}
        />
        {ad === null && ads.data && !ads.data.ok && (
          <p className="mt-1 text-[11px] text-text-tertiary">{META_UNAVAILABLE_HINT[ads.data.error]}</p>
        )}
        {ads.data?.ok && ads.data.truncated && (
          <p className="mt-1 text-[11px] text-text-tertiary">{META_TRUNCATED_HINT}</p>
        )}
      </Field>
    </div>
  );
}
